import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { IconComponent } from '../../../../components/icon/icon.component';
import { Notification } from '../../../../interfaces/notification.interface';
import { NotificationActions } from '../../../../store/actions/notification.actions';
import {
  selectNotificationItems,
  selectUnreadNotificationsCount,
} from '../../../../store/selectors/notification.selector';
import { getNotificationLink } from '../../../../utils/notification-link';

export type NotificationFilter = 'ALL' | 'READ' | 'UNREAD';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  imports: [CommonModule, IconComponent],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  public isOpen = false;

  public readonly filterOptions: { label: string; value: NotificationFilter }[] = [
    { label: 'All', value: 'ALL' },
    { label: 'Unread', value: 'UNREAD' },
    { label: 'Read', value: 'READ' },
  ];

  public readonly filter$ = new BehaviorSubject<NotificationFilter>('ALL');

  public unreadCount$ = this.store.select(selectUnreadNotificationsCount);
  public items$ = this.store.select(selectNotificationItems);

  public filteredItems$ = combineLatest([this.items$, this.filter$]).pipe(
    map(([items, filter]) => {
      if (filter === 'UNREAD') {
        return items.filter((item) => !item.readAt);
      }

      if (filter === 'READ') {
        return items.filter((item) => !!item.readAt);
      }

      return items;
    }),
  );

  public constructor(
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.store.dispatch(NotificationActions.startPolling());
  }

  public ngOnDestroy(): void {
    this.store.dispatch(NotificationActions.stopPolling());
  }

  public toggle(): void {
    this.isOpen = !this.isOpen;
  }

  public close(): void {
    this.isOpen = false;
  }

  public async viewNotification(notification: Notification): Promise<void> {
    if (!notification.readAt) {
      this.store.dispatch(
        NotificationActions.markRead({ id: notification.id }),
      );
    }

    const link = getNotificationLink(notification);

    if (!link) {
      return;
    }

    this.close();
    await this.router.navigateByUrl(link);
  }

  public markAllRead(): void {
    this.store.dispatch(NotificationActions.markAllRead());
  }

  public setFilter(filter: NotificationFilter): void {
    this.filter$.next(filter);
  }
}
